import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { GroupItem, IFilters } from '../../../types/typesVPO';
import ExportToExcelRegion from '../../exportExcel/ExportExcelReportRegion';
import FilterForPlanAction from '../../planAction/filterForPlanAction/FilterForPlanAction';
import { filterConfigReportRegion, filtersOpenReportRegion, years } from './filterDataRegion';
import { TableRegionReport } from './TableRegionReport';
import { getInitDataVpo } from 'src/modules/vpo/actions';
import { ContentTypeIdColumn } from 'src/modules/vpo/api/config';
import { selectCurrentUser } from 'src/modules/users/selector';
import Loading from 'src/components/Loading';
import { selectModelData } from '../../../store/filtersData/selector';
import { fetchData } from '../../../store/filtersData/actions';
import { transformDataForFilters, transformDataForFiltersInCode } from '../../../utils/plan_actions/plan_actions';
import { fetchReportsRequest } from '../../../store/reports/actions';
import { selectReportsData, selectReportsLoading } from '../../../store/reports/selectors';

export default function ReportRegionMain() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => selectCurrentUser(state));

  const getCurrentDate = useCallback(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    return {
      year: {
        id: currentYear - 2025 + 1,
        name: currentYear.toString(),
      },
    };
  }, []);

  const defaultFilterValues = useMemo(
    () => ({
      year: {
        selected: [getCurrentDate().year.name],
        selectedIds: [getCurrentDate().year.id],
        search: '',
      },
      group: {
        selected: [],
        selectedIds: [],
        search: '',
      },
      department: {
        selected: [],
        selectedIds: [],
        search: '',
      },
    }),
    [getCurrentDate]
  );

  const [currentFilters, setCurrentFilters] = useState<IFilters>(defaultFilterValues);
  const initialFiltersRef = useRef<IFilters>(defaultFilterValues);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const lastRequestParamsRef = useRef<string>('');

  const factoryVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-factories'));
  const staticGroups = useAppSelector((state: any) => selectModelData(state, 'vpo-groups'));
  const FilterColumns = useAppSelector((state: any) => selectModelData(state, 'vpo-columns'))
    ?.slice()
    .sort((a, b) => a.SortNumber - b.SortNumber);
  const subdivisionVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-departments'))?.slice()
    .sort((a, b) => b.SortNumber - a.SortNumber);

  const reportsData = useAppSelector(selectReportsData);
  const reportsLoading = useAppSelector(selectReportsLoading);

  //! Получаю группы
  useEffect(() => {
    dispatch(
      fetchData(
        'vpo-groups',
        {},
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
  }, [dispatch]);

  //! При изменении группы получаю колонны и заводы
  useEffect(() => {
    dispatch(fetchData('vpo-columns', {}, { page: 1, pageSize: 100 }));
    if (currentFilters?.group?.selectedIds) {
      dispatch(fetchData('vpo-factories', { group: currentFilters?.group?.selectedIds }, { page: 1, pageSize: 100 }));
    }
  }, [currentFilters?.group?.selectedIds, dispatch]);

  //! При изменении заводов получаю департаменты
  useEffect(() => {
    if (factoryVPO && factoryVPO.length > 0) {
      const factoryIds = factoryVPO.map((factory) => factory.id);
      dispatch(fetchData('vpo-departments', { factories: factoryIds }, { page: 1, pageSize: 100 }));
    }
  }, [factoryVPO, dispatch]);

  //! Выбор группы согласно групп доступа
  const getDefaultGroup = useCallback(() => {
    if (!user?.infoVPO) {
      return null;
    }

    if (user.infoVPO.zone || user.infoVPO.adminRegion) {
      if (user.infoVPO.adminRegion && user.infoVPO.directoryTitle?.length > 0) {
        const userGroupTitles = user.infoVPO.directoryTitle;
        const userGroups = staticGroups.filter((group) => userGroupTitles.includes(group.title));

        const sortedGroups = [...userGroups].sort((a, b) => {
          const getPriority = (group: GroupItem) => {
            if (group.title === 'BOP') return 1;
            if (group.title === 'VOP') return 2;
            return 3;
          };
          const priorityA = getPriority(a);
          const priorityB = getPriority(b);
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          return a.title.localeCompare(b.title);
        });
        return sortedGroups.length > 0 ? sortedGroups[0] : null;
      }
      return null;
    }
    const userGroupTitles = user.infoVPO.directoryTitle || [];
    const userGroups = staticGroups.filter((group) => userGroupTitles.includes(group.title));

    if (userGroups.length === 0 && user.infoVPO.factory?.length > 0) {
      const userFactoryIds = user.infoVPO.factory.map((f: { factoryID: number }) => f.factoryID);

      for (const factory of factoryVPO) {
        if (userFactoryIds.includes(factory.ID) && factory.VPOgroup?.[0]?.Title) {
          const factoryGroupTitle = factory.VPOgroup[0].title;
          const factoryGroup = staticGroups.find((g) => g.title === factoryGroupTitle);
          if (factoryGroup) {
            return factoryGroup;
          }
        }
      }
    }

    const sortedGroups = [...userGroups].sort((a, b) => {
      const getPriority = (group: GroupItem) => {
        if (group.Title === 'BOP') return 1;
        if (group.Title === 'VOP') return 2;
        return 3;
      };
      const priorityA = getPriority(a);
      const priorityB = getPriority(b);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.Title.localeCompare(b.Title);
    });
    return sortedGroups.length > 0 ? sortedGroups[0] : null;
  }, [user?.infoVPO, factoryVPO, staticGroups]);

  useEffect(() => {
    if (!filtersInitialized && user?.infoVPO && factoryVPO.length > 0) {
      const currentDate = getCurrentDate();
      const defaultGroup = getDefaultGroup();

      const initialFilter = {
        year: {
          selected: [currentDate.year.name],
          selectedIds: [currentDate.year.id],
          search: '',
        },
        group: {
          selected: defaultGroup ? [defaultGroup.title] : [],
          selectedIds: defaultGroup ? [defaultGroup.id] : [],
          search: '',
        },
        department: {
          selected: [],
          selectedIds: [],
          search: '',
        },
      };

      initialFiltersRef.current = initialFilter;
      setCurrentFilters(initialFilter);
      setFiltersInitialized(true);
    }
  }, [factoryVPO, user?.infoVPO, getDefaultGroup, getCurrentDate, filtersInitialized]);

  //! Мемоизируем список заводов чтобы не пересоздавался при каждом рендере
  const factoryIds = useMemo(() => {
    return factoryVPO.map((f: any) => f.id).sort((a, b) => a - b);
  }, [factoryVPO]);

  //! Мемоизируем список колонок
  const columnIds = useMemo(() => {
    return FilterColumns?.map((col: any) => col.id).sort((a, b) => a - b) || [];
  }, [FilterColumns]);

  //! Загрузка данных отчетов с бэкенда (с SSE для годовых отчетов)
  useEffect(() => {
    if (!filtersInitialized || columnIds.length === 0 || factoryIds.length === 0 || !currentFilters) return;
    const year = currentFilters?.year?.selected?.[0];
    const subdivisions = currentFilters?.department?.selectedIds && currentFilters.department.selectedIds.length > 0 ? currentFilters.department.selectedIds : undefined;
    const group = currentFilters?.group?.selectedIds?.[0];

    const requestParams = JSON.stringify({ factories: factoryIds, columns: columnIds, year, subdivisions, group });
    if (factoryIds.length > 0 && columnIds.length > 0 && year && requestParams !== lastRequestParamsRef.current) {
      lastRequestParamsRef.current = requestParams;

      dispatch(fetchReportsRequest(factoryIds, columnIds, undefined, year, subdivisions, group));
    }
  }, [dispatch, filtersInitialized, currentFilters, factoryIds, columnIds]);

  const handleFiltersChange = useCallback(
    (newFilters: React.SetStateAction<IFilters>) => {
      if (!isEqual(currentFilters, newFilters)) {
        // console.log('Новые фильтры ОТЧЕТЫ:', newFilters);
        setCurrentFilters(newFilters);
      }
    },
    [currentFilters]
  );

  const transformedroups = React.useMemo(() => transformDataForFilters(staticGroups), [staticGroups]);
  const transformeDepartments = React.useMemo(() => transformDataForFilters(subdivisionVPO), [subdivisionVPO]);
  const transformedFactories = React.useMemo(() => transformDataForFilters(factoryVPO), [factoryVPO]);
  const transformedColumns = React.useMemo(() => transformDataForFiltersInCode(FilterColumns), [FilterColumns]);

  const filterConfigs = useMemo(
    () => ({
      year: { items: years },
      group: { items: transformedroups },
      department: { items: transformeDepartments },
    }),
    [transformeDepartments]
  );

  const filtersMap = useMemo(
    () => ({
      year: years,
      group: transformedroups,
      department: transformeDepartments,
    }),
    [transformeDepartments]
  );

  if (!filtersInitialized) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <Loading />
      </div>
    );
  }

  return (
    <div className='flex justify-between'>
      <div className='w-[320px] mr-2 bg-white'>
        <FilterForPlanAction
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={initialFiltersRef.current}
          filtersOpen={filtersOpenReportRegion}
          filtersConfig={filterConfigReportRegion}
          responsibleFil={false}
          selectedGropu={true}
          heightFilter={'max-h-screen'}
        />
      </div>
      <div className='flex-1 min-w-0 m-4'>
        <div className='flex items-center justify-between mb-6 text-gray-600'>
          <div className='flex items-center font-normal text-primary'>
            <span className='pr-3'>
              <ReportIcon />
            </span>
            <span className='cursor-pointer' onClick={() => navigate('/dashboard/6/reports')}>
              Отчеты
            </span>
            <span className='mx-2'>→</span>
            <span>Отчет по региону</span>
            <span className='mx-2'>→</span>
            <span>{currentFilters.group?.selected[0] || 'BOP'}</span>
          </div>
          <ExportToExcelRegion reportsData={reportsData} groupTitle={currentFilters.group?.selected[0] || ''} FilterColumns={transformedColumns} FactoryVPO={transformedFactories} />
        </div>
        <div className='mb-2 min-w-0 relative'>
          <TableRegionReport
            FactoryVPO={transformedFactories}
            FilterColumns={transformedColumns}
            currentGroup={currentFilters.group?.selected[0]}
            reportsData={reportsData}
            reportsLoading={reportsLoading}
          />
        </div>
      </div>
    </div>
  );
}
